import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, Save, Calendar, Clock, Users, FileText,
  AlertCircle, CheckCircle, DollarSign, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const MEETING_TYPES = [
  'Onboarding',
  'Commercial Negotiation',
  'Incentive Revision',
  'Collection Follow-up',
  'Issue Escalation',
  'Relationship Review'
];

const MEETING_MODES = ['In-person', 'Call', 'Virtual'];
const RISK_LEVELS = ['Low', 'Medium', 'High'];

const BDMeetingForm = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedBuilderId = searchParams.get('builder_id');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [builders, setBuilders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bdOwners, setBdOwners] = useState([]);
  
  const [formData, setFormData] = useState({
    builder_id: preselectedBuilderId || '',
    bd_project_id: '',
    meeting_type: 'Onboarding',
    mode: 'In-person',
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_time: '',
    attendees: '',
    agenda: '',
    discussion_summary: '',
    brokerage_change: false,
    incentive_change: false,
    payment_terms_change: false,
    commercial_notes: '',
    collection_amount_discussed: '',
    collection_commitment_date: '',
    collection_risk_level: 'Low',
    collection_notes: '',
    next_action_description: '',
    next_action_owner: '',
    next_action_deadline: '',
  });

  useEffect(() => {
    fetchBuilders();
    fetchBdOwners();
  }, []);

  useEffect(() => {
    if (formData.builder_id) {
      fetchProjects(formData.builder_id);
    } else {
      setProjects([]);
    }
  }, [formData.builder_id]);

  const fetchBuilders = async () => {
    try {
      const res = await api.getBuilders({ status: 'Active', limit: 100 });
      setBuilders(res.data.builders || []);
    } catch (error) {
      console.error('Failed to load builders:', error);
    }
  };

  const fetchProjects = async (builderId) => {
    try {
      const res = await api.getBDProjects({ builder_id: builderId });
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const fetchBdOwners = async () => {
    try {
      const res = await api.getBDOwners();
      setBdOwners(res.data.bd_owners || []);
    } catch (error) {
      console.error('Failed to load BD owners:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.builder_id) {
      toast.error('Please select a builder');
      return;
    }
    if (!formData.next_action_description.trim()) {
      toast.error('Next Action is mandatory');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        collection_amount_discussed: formData.collection_amount_discussed 
          ? parseFloat(formData.collection_amount_discussed) 
          : null,
      };
      
      await api.createBDMeeting(payload);
      toast.success('Meeting logged successfully');
      
      if (preselectedBuilderId) {
        navigate(`/bd/builders/${preselectedBuilderId}`);
      } else {
        navigate('/bd/meetings');
      }
    } catch (error) {
      console.error('Failed to save meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to save meeting');
    } finally {
      setSaving(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-red-600';
      case 'Medium': return 'text-amber-600';
      default: return 'text-emerald-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Log Meeting</h1>
              <p className="text-sm text-slate-500">Record builder interaction</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Meeting Details */}
        <Card className="p-4">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-purple-600" />
            Meeting Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Builder *</Label>
              <Select 
                value={formData.builder_id} 
                onValueChange={(v) => setFormData({ ...formData, builder_id: v, bd_project_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select builder" />
                </SelectTrigger>
                <SelectContent>
                  {builders.map(b => (
                    <SelectItem key={b.builder_id} value={b.builder_id}>
                      {b.builder_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Project (Optional)</Label>
              <Select 
                value={formData.bd_project_id || "none"} 
                onValueChange={(v) => setFormData({ ...formData, bd_project_id: v === "none" ? "" : v })}
                disabled={!formData.builder_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific project</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.bd_project_id} value={p.bd_project_id}>
                      {p.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Meeting Type *</Label>
              <Select 
                value={formData.meeting_type} 
                onValueChange={(v) => setFormData({ ...formData, meeting_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEETING_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Mode *</Label>
              <Select 
                value={formData.mode} 
                onValueChange={(v) => setFormData({ ...formData, mode: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEETING_MODES.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.meeting_date}
                onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={formData.meeting_time}
                onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <Label>Attendees</Label>
              <Input
                value={formData.attendees}
                onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                placeholder="Names of people in the meeting"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Agenda</Label>
              <Textarea
                value={formData.agenda}
                onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                placeholder="Meeting agenda"
                rows={2}
              />
            </div>
          </div>
        </Card>

        {/* A. Discussion Summary */}
        <Card className="p-4">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            A. Discussion Summary
          </h2>
          <Textarea
            value={formData.discussion_summary}
            onChange={(e) => setFormData({ ...formData, discussion_summary: e.target.value })}
            placeholder="Summary of key discussion points..."
            rows={4}
          />
        </Card>

        {/* B. Commercial Update */}
        <Card className="p-4">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <DollarSign size={18} className="text-emerald-600" />
            B. Commercial Update
          </h2>
          
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.brokerage_change}
                  onChange={(e) => setFormData({ ...formData, brokerage_change: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <span className="text-sm">Brokerage Change?</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.incentive_change}
                  onChange={(e) => setFormData({ ...formData, incentive_change: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <span className="text-sm">Incentive Change?</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.payment_terms_change}
                  onChange={(e) => setFormData({ ...formData, payment_terms_change: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <span className="text-sm">Payment Terms Change?</span>
              </label>
            </div>

            <Textarea
              value={formData.commercial_notes}
              onChange={(e) => setFormData({ ...formData, commercial_notes: e.target.value })}
              placeholder="Commercial discussion notes..."
              rows={2}
            />
          </div>
        </Card>

        {/* C. Collection Update */}
        <Card className="p-4">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <AlertCircle size={18} className="text-amber-600" />
            C. Collection Update
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Amount Discussed (₹)</Label>
              <Input
                type="number"
                value={formData.collection_amount_discussed}
                onChange={(e) => setFormData({ ...formData, collection_amount_discussed: e.target.value })}
                placeholder="0"
              />
            </div>

            <div>
              <Label>Commitment Date</Label>
              <Input
                type="date"
                value={formData.collection_commitment_date}
                onChange={(e) => setFormData({ ...formData, collection_commitment_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Risk Level</Label>
              <Select 
                value={formData.collection_risk_level} 
                onValueChange={(v) => setFormData({ ...formData, collection_risk_level: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">🟢 Low</SelectItem>
                  <SelectItem value="Medium">🟡 Medium</SelectItem>
                  <SelectItem value="High">🔴 High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-3">
              <Label>Collection Notes</Label>
              <Textarea
                value={formData.collection_notes}
                onChange={(e) => setFormData({ ...formData, collection_notes: e.target.value })}
                placeholder="Collection-related notes..."
                rows={2}
              />
            </div>
          </div>
        </Card>

        {/* D. Next Action (Mandatory) */}
        <Card className="p-4 border-2 border-purple-200 bg-purple-50/50">
          <h2 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
            <CheckCircle size={18} className="text-purple-600" />
            D. Next Action (Mandatory)
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-purple-700">Action Description *</Label>
              <Textarea
                value={formData.next_action_description}
                onChange={(e) => setFormData({ ...formData, next_action_description: e.target.value })}
                placeholder="What needs to be done next?"
                rows={2}
                className="border-purple-200 focus:border-purple-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Owner</Label>
                <Select 
                  value={formData.next_action_owner} 
                  onValueChange={(v) => setFormData({ ...formData, next_action_owner: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {bdOwners.map(owner => (
                      <SelectItem key={owner.user_id} value={owner.user_id}>
                        {owner.rep_name || owner.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Deadline</Label>
                <Input
                  type="date"
                  value={formData.next_action_deadline}
                  onChange={(e) => setFormData({ ...formData, next_action_deadline: e.target.value })}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Submit */}
        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            disabled={saving}
          >
            <Save size={16} className="mr-1" />
            {saving ? 'Saving...' : 'Save Meeting'}
          </Button>
          <Button 
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BDMeetingForm;
